import {
  type Data,
  DragOverlay,
  type UniqueIdentifier,
  useDraggable,
} from "@dnd-kit/core";
import { cloneElement } from "react";
import { createPortal } from "react-dom";

type RenderFunction = (props: { isDragging: boolean }) => React.ReactNode;

type DraggableProps<T> = {
  id: UniqueIdentifier;
  data: T;
  dragOverlayClassName?: string;
  overlayProps?: React.ComponentProps<typeof DragOverlay>;
  tableRef?: React.MutableRefObject<HTMLTableElement | null>;
} & Omit<React.HtmlHTMLAttributes<HTMLSpanElement>, "id"> &
  (
    | { children: React.ReactNode; render?: never }
    | { children?: never; render: RenderFunction }
  );

export const Draggable = <T extends Data>({
  children,
  className,
  data,
  dragOverlayClassName,
  id,
  overlayProps = {},
  render,
  tableRef,
  ...props
}: DraggableProps<T>) => {
  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
    id,
    data,
  });

  // Left here for backwards compatibility. Use `isDragging` from the 'render' render function prop instead.
  const draggingStyles = "isDragging";

  // If render prop is provided, use it instead of children
  if (render) {
    const renderedContent = render({ isDragging });

    return (
      <>
        {cloneElement(renderedContent as React.ReactElement, {
          ref: setNodeRef,
          style: {
            opacity: isDragging ? 0.7 : undefined,
            ...((renderedContent as React.ReactElement).props.style || {}),
          },
          className: [
            className,
            (renderedContent as React.ReactElement).props.className,
            isDragging ? draggingStyles : "",
          ].join(" "),
          ...listeners,
          ...attributes,
          ...props,
        })}
        {isDragging
          ? createPortal(
              <DragOverlay {...overlayProps}>
                {cloneElement(renderedContent as React.ReactElement, {
                  style: {
                    ...((renderedContent as React.ReactElement).props.style ||
                      {}),
                  },
                  className: [
                    className,
                    dragOverlayClassName,
                    draggingStyles,
                    (renderedContent as React.ReactElement).props.className,
                  ].join(" "),
                  ...props,
                })}
              </DragOverlay>,
              tableRef?.current ?? document.body
            )
          : null}
      </>
    );
  }

  // Default implementation with children
  return (
    <>
      {cloneElement(children as React.ReactElement, {
        ref: setNodeRef,
        style: {
          opacity: isDragging ? 0.7 : undefined,
          ...((children as React.ReactElement).props.style || {}),
        },
        className: [
          className,
          (children as React.ReactElement).props.className,
          isDragging ? draggingStyles : "",
        ].join(" "),
        ...listeners,
        ...attributes,
        ...props,
      })}
      {isDragging
        ? createPortal(
            <DragOverlay {...overlayProps}>
              {cloneElement(children as React.ReactElement, {
                style: {
                  ...((children as React.ReactElement).props.style || {}),
                },
                className: [
                  className,
                  dragOverlayClassName,
                  draggingStyles,
                  (children as React.ReactElement).props.className,
                ].join(" "),
                ...props,
              })}
            </DragOverlay>,
            tableRef?.current ?? document.body
          )
        : null}
    </>
  );
};
